import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Photographers we want to surface, in rotation order.
const PHOTOGRAPHER_NAMES = [
  "Terry O'Neill",
  'Michael Joseph',
  'Drew Doggett',
  'Sebastian Magnani',
  'Kate Garner',
  'Chris Levine',
];

const FeaturedPhotographer = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const trackRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const results = await Promise.all(
        PHOTOGRAPHER_NAMES.map(async (name) => {
          try {
            const res = await fetch(
              `${API_URL}/api/artworks?artist=${encodeURIComponent(name)}&available=true&limit=60`
            );
            const works = await res.json();
            // Belt-and-braces: still skip anything whose title hints at a back-of-print
            const usable = (works || []).filter(
              (w) =>
                w.imageUrl &&
                !/vintage print|signature|certificate/i.test(w.title || '')
            );
            // Prefer any artist-featured picks so the gallery can pin
            // headliners (e.g. Sebastian Magnani's Vol III) to the top slot.
            usable.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
            return usable.slice(0, 4);
          } catch {
            return [];
          }
        })
      );
      // Round-robin so no two adjacent slides are by the same photographer
      const buckets = results.map((arr) => [...arr]);
      const interleaved = [];
      let added = true;
      while (added) {
        added = false;
        for (const bucket of buckets) {
          const next = bucket.shift();
          if (next) {
            interleaved.push(next);
            added = true;
          }
        }
      }
      if (!cancelled) {
        setPhotos(interleaved);
        setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const scrollByCard = (direction) => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.querySelector('[data-carousel-card]');
    const step = card ? card.getBoundingClientRect().width + 16 : 260;
    track.scrollBy({ left: direction * step, behavior: 'smooth' });
  };

  if (loading) return null;
  if (photos.length === 0) return null;

  return (
    <section
      className="bg-white py-20 lg:py-24 border-y border-neutral-200"
      data-testid="featured-photographer"
    >
      <div className="max-w-[1400px] mx-auto px-6">
        {/* Header */}
        <div className="flex items-end justify-between gap-4 mb-10">
          <div>
            <p className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 mb-3">
              Featured photography
            </p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-tight text-neutral-900">
              Iconic frames from our archive
            </h2>
          </div>
          <div className="hidden sm:flex items-center gap-2 shrink-0">
            <button
              onClick={() => scrollByCard(-1)}
              aria-label="Previous"
              data-testid="featured-prev"
              className="w-10 h-10 rounded-full border border-neutral-300 hover:bg-neutral-900 hover:text-white hover:border-neutral-900 transition-colors flex items-center justify-center"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => scrollByCard(1)}
              aria-label="Next"
              data-testid="featured-next"
              className="w-10 h-10 rounded-full border border-neutral-300 hover:bg-neutral-900 hover:text-white hover:border-neutral-900 transition-colors flex items-center justify-center"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Track */}
        <div
          ref={trackRef}
          className="flex gap-4 overflow-x-auto pb-3 snap-x snap-mandatory scroll-smooth scrollbar-hide -mx-6 px-6"
          style={{ scrollbarWidth: 'none' }}
          data-testid="featured-track"
        >
          {photos.map((p) => {
            const src = p.imageUrl.startsWith('http') ? p.imageUrl : `${API_URL}${p.imageUrl}`;
            return (
              <div
                key={p.id}
                data-carousel-card
                className="snap-start shrink-0 w-[60%] sm:w-[38%] md:w-[28%] lg:w-[22%]"
              >
                <Link
                  to={`/artwork/${p.slug || p.id}`}
                  data-testid={`featured-photo-${p.id}`}
                  className="group block"
                >
                  <div className="relative aspect-square overflow-hidden bg-neutral-100 mb-3">
                    <img
                      src={src}
                      alt={`${p.title} — ${p.artistName}`}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                    />
                  </div>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-1">
                    {p.artistName}
                  </p>
                  <p className="text-sm font-serif text-neutral-900 leading-snug line-clamp-2 mb-1">
                    {p.title}
                  </p>
                  <p className="text-xs text-neutral-500">
                    {p.priceDisplay || 'Price on Request'}
                  </p>
                </Link>
              </div>
            );
          })}

          {/* Trailing CTA */}
          <div
            data-carousel-card
            className="snap-start shrink-0 w-[60%] sm:w-[38%] md:w-[28%] lg:w-[22%]"
          >
            <Link
              to="/shop?category=Photography"
              className="group flex flex-col h-full justify-center items-start aspect-square mb-3 border border-neutral-300 p-6 hover:bg-neutral-900 hover:text-white hover:border-neutral-900 transition-colors"
              data-testid="featured-view-all"
            >
              <p className="text-[10px] uppercase tracking-[0.25em] mb-3 opacity-60">
                Browse the archive
              </p>
              <p className="font-serif text-xl leading-tight mb-4">
                Every photograph in the collection
              </p>
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] mt-auto">
                View all
                <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedPhotographer;
